import type { UseMutationResult } from "@tanstack/react-query";
import { isEqual } from "lodash";
import { type UpdateReport } from "../../api/routes";
import type * as client from "../../client";

type SaveParams = {
  editedContact: client.ReportContact;
  originalContact: client.ReportContact;
  emailMatchedContact: client.ReportContact | null;
  // If provided, the modal's report id to reassign after save.
  // `currentAssigneeId` is the assignee id on the report when the modal
  // opened; it's used as the baseline for detecting reassign-only actions.
  currentAssigneeId?: string | null;
  updateReportMutation: UseMutationResult<
    client.UpdateReportResponse,
    unknown,
    UpdateReport,
    unknown
  >;

  // TODO: Maybe these should live elsewhere, closer to the
  // other mutations?

  // Mutations for creating and updating CRM contacts. The helper calls
  // `mutateAsync` on these so that react-query cache updates and side
  // effects run consistently with the app's hooks.
  createContactMutation: UseMutationResult<
    client.CreateContactResponse,
    unknown,
    client.CreateContactRequest,
    unknown
  >;
  updateContactMutation: UseMutationResult<
    client.UpdateContactResponse,
    unknown,
    { id: string; body: client.UpdateContactRequest },
    unknown
  >;
};

// Build concrete `UpdateContactRequest` objects from `Contact` so callers
// can compare payloads that will be sent to the API. Exported so other
// modules (e.g. the edit modal) can reuse the same normalization logic.
export const makeUpdateRequest = (
  c: client.ReportContact
): client.UpdateContactRequest => ({
  full_name: c.full_name || null,
  organization_name:
    c.organization_name === null ? null : c.organization_name || null,
  office_phone: c.office_phone || null,
  cell_phone: c.cell_phone || null,
  role: c.role || null,
});

export async function maybeSaveContactAndReassign({
  editedContact,
  originalContact,
  emailMatchedContact,
  currentAssigneeId,
  updateReportMutation,
  createContactMutation,
  updateContactMutation,
}: SaveParams): Promise<client.ReportContact | null> {
  // Build concrete `UpdateContactRequest` objects from `Contact` so we can
  // compare the actual payload that would be sent to the API
  const editedReq = makeUpdateRequest(editedContact); // Use exported helper
  const originalReq = makeUpdateRequest(originalContact); // Use exported helper
  const computedHasChanges = !isEqual(editedReq, originalReq);

  let resultingContact: client.ReportContact | null = null;

  if (emailMatchedContact && editedContact.id === "") {
    resultingContact = emailMatchedContact;
  } else if (editedContact.id === "") {
    const createBody: client.CreateContactRequest = {
      full_name:
        (editedContact.full_name && editedContact.full_name.trim()) ?? null,
      email: editedContact.email.trim().toLowerCase(),
      contact_type: editedContact.contact_type,
      organization_name:
        (editedContact.organization_name &&
          editedContact.organization_name.trim()) ??
        null,
      office_phone:
        (editedContact.office_phone && editedContact.office_phone.trim()) ??
        null,
      cell_phone:
        (editedContact.cell_phone && editedContact.cell_phone.trim()) ?? null,
      role: (editedContact.role && editedContact.role.trim()) ?? null,
    };

    const resp = await createContactMutation.mutateAsync(createBody);
    resultingContact = resp.contact;
  } else {
    if (computedHasChanges && editedContact.id && editedContact.id !== "") {
      const reqBody: client.UpdateContactRequest = editedReq;
      const resp = await updateContactMutation.mutateAsync({
        id: editedContact.id,
        body: reqBody,
      });
      resultingContact = resp.contact;
    } else {
      resultingContact = editedContact;
    }
  }

  // Determine whether this operation should reassign the report. Use the
  // explicit `currentAssigneeId` when provided by the caller; otherwise fall
  // back to the `originalContact` passed in. Compute this after obtaining
  // `resultingContact` so that create/select flows are accounted for.
  const baseOriginalAssigneeId = currentAssigneeId ?? originalContact.id;

  // TODO: It might be cleaner to separate the contact save and broker update, since
  // this is specific to the broker use case.
  if (resultingContact && editedContact.contact_type === "broker") {
    const isReassigningNow =
      resultingContact.id !== (baseOriginalAssigneeId ?? "");
    if (isReassigningNow) {
      await updateReportMutation.mutateAsync({
        brokerContact: { new_broker_contact_id: resultingContact.id },
      });
    }
  }
  return resultingContact;
}
